import React, { useCallback, useEffect, useMemo, useState } from 'react';

import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import CameraIcon from 'apps/web_forms/assets/cameraicon.svg';
import ErrorMessage from 'apps/web_forms/components/commons/ErrorMessage';
import ImageUploadElement from 'apps/web_forms/components/commons/ImageUploadElement';
import useChangeValue from 'apps/web_forms/hooks/useChangeValue';
import useResourceUpdateHighlight from 'apps/web_forms/hooks/useResourceUpdateHighlight';
import { selectFieldErrorById } from 'apps/web_forms/hooks/useGlobalSelector';
import useRules from 'apps/web_forms/hooks/useRules';
import {
  addImageToSubmit,
  removeImageDraftValue, removeImageToSubmit, setCurrentSelectedMultimediaInput
} from 'apps/web_forms/slices/multimediaSlice';
import customPropTypes from 'apps/web_forms/utils/customPropTypes';
import { removeDraftAnswers } from 'apps/web_forms/slices/formSlice';
import _ from 'lodash';

const ImageInput = ({ element, formContext, onChangeValue, toggleActiveValue, showContent }) => {
  const {
    title,
    identifier,
    initialAnswer: _initialAnswer, // eslint-disable-line no-unused-vars
    hint,
    hidden, 
    validate_msg: validateMsg,
    camera_only: cameraOnly,
    extra_large: extraLarge,
  } = element;
  const { register, unregister } = formContext;

  const { changeValue, currentValue } = useChangeValue(
    element,
    onChangeValue,
    formContext
  );

  const errorMessage = useSelector((state) => selectFieldErrorById(state, identifier));
  const imageToSubmit = useSelector((state) => state.multimedia.imagesToSubmit[identifier]);
  const imageDraftValue = useSelector((state) => state.multimedia.imageDraftValues[identifier]);
  const imageFromCamera = useSelector((state) => state.multimedia.imageInputValues[identifier]);
  const draftAnswer = useSelector((state) => state.form.draftAnswers[identifier]);

  const { hasResourceUpdateHighlight, removeResourceUpdateHighlight } =
    useResourceUpdateHighlight({ fieldIdentifier: identifier });

  const dispatch = useDispatch();

  const [{ isActive, isRequired, isElementValid, isReadOnly }] =
    useRules(element, formContext);


  const handleAddImageToSubmit = (image) => {
    dispatch(
      addImageToSubmit({
        key: element.identifier,
        value: image,
      })
    );
    if(imageDraftValue){
      dispatch(removeImageDraftValue(element.identifier));
    }
  };

  const handleRemoveImageToSubmit = () => {
    dispatch(removeImageToSubmit(element.identifier));
    if(draftAnswer){
      dispatch(removeDraftAnswers(element.identifier));
    }
  };

  const handleCameraButtonClick = useCallback(() => {
    if (!isReadOnly) {
      dispatch(setCurrentSelectedMultimediaInput({ element }));
    }
  }, [isReadOnly]);

  const hasValue = !!imageToSubmit || !!imageDraftValue || !!_initialAnswer
  const imagesToSubmitForInput = useMemo(
    () => (imageToSubmit ? { [identifier]: imageToSubmit } : {}),
    [identifier, imageToSubmit]
  );
  const imageDraftValuesForInput = useMemo(
    () => (imageDraftValue ? { [identifier]: imageDraftValue } : {}),
    [identifier, imageDraftValue]
  );

  useEffect(() => {
    register(identifier, {
      validate: () => isElementValid || validateMsg || false,
      required: isRequired && !hasValue ? 'This field is required' : false,
    })
  }, [identifier, isElementValid, validateMsg, isRequired, hasValue]);

  useEffect(() => {
    toggleActiveValue(identifier, isActive);
  }, [isActive]);

  useEffect(() => {
    changeValue(_initialAnswer, false)
  }, [])

  if (!isActive || hidden || !showContent) return null;

  const thumbnail =
    imageToSubmit || imageDraftValue?.imageUrl;

  return (
    <div className="react-form-control">
      <label htmlFor={identifier}>{title}</label>
      <ImageUploadElement
        id={identifier}
        hint={hint}
        cameraButtonIcon={CameraIcon}
        cameraOnly={cameraOnly}
        thumbnailImage={thumbnail}
        isRequired={isRequired}
        isInvalid={errorMessage !== undefined}
        hasResourceUpdateHighlight={hasResourceUpdateHighlight}
        removeResourceUpdateHighlight={removeResourceUpdateHighlight}
        isReadOnly={isReadOnly}
        imageFromCamera={imageFromCamera}
        onCameraButtonClick={handleCameraButtonClick}
        onNewImage={handleAddImageToSubmit}
        removeImageToSubmit={handleRemoveImageToSubmit}
        changeValue={changeValue}
        compressImage={!extraLarge}
        initialAnswer={_initialAnswer}
        hasValue={hasValue}
        imageDraftValues={imageDraftValuesForInput}
        imagesToSubmit={imagesToSubmitForInput}
        formContext={formContext}
      />
      {errorMessage !== undefined && (
        <ErrorMessage id={`${identifier}_errmsg`}>{errorMessage}</ErrorMessage>
      )}
    </div>
  );
};

ImageInput.propTypes = {
  element: PropTypes.shape({
    title: PropTypes.string,
    identifier: PropTypes.string,
    initialAnswer: PropTypes.string,
    hint: PropTypes.string,
    hidden: PropTypes.bool,
    validate_msg: PropTypes.string,
    camera_only: PropTypes.bool,
    extra_large: PropTypes.bool,
  }).isRequired,
  formContext: customPropTypes.formContext.isRequired,
  onChangeValue: PropTypes.func.isRequired,
};

export default React.memo(ImageInput);
